import { resolveScannedDryingTechnology, type DryingTechnology } from "./dryerTechnology";

// Map a free-text OCR productType (e.g. "Refrigerator", "Washer Dryer") to the
// closest company-configured category name (e.g. "Fridge", "Washer Dryer").
// FIX-289: an exact name wins, and a washer dryer is never matched to "Dryer"
// or "Washing Machine" just because those names are contained in it.
const WASHER_DRYER_RE = /washer[\s_-]?dr(y|i)er|wasch-?trockner|was-?droger/i;

export function matchCompanyCategory(productType: string | undefined, names: string[]): string {
  if (!productType || names.length === 0) return "";
  const t = productType.trim().toLowerCase();
  const lowered = names.map((n) => ({ n, l: n.trim().toLowerCase() }));

  const exact = lowered.find((x) => x.l === t);
  if (exact) return exact.n;

  if (WASHER_DRYER_RE.test(t)) {
    const wd = lowered.find((x) => WASHER_DRYER_RE.test(x.l));
    return wd ? wd.n : "";
  }

  // Contains, longest name first so the most specific category wins.
  const direct = lowered
    .filter((x) => x.l.includes(t) || t.includes(x.l))
    .sort((a, b) => b.l.length - a.l.length)[0];
  if (direct) return direct.n;

  const buckets: Array<{ keys: string[]; match: string[] }> = [
    { keys: ["fridge", "refriger"], match: ["fridge", "refriger"] },
    { keys: ["freezer"], match: ["freezer"] },
    { keys: ["dishwash"], match: ["dishwash"] },
    { keys: ["dryer", "tumble"], match: ["dryer", "tumble"] },
    { keys: ["wash"], match: ["wash"] },
    { keys: ["oven"], match: ["oven"] },
    { keys: ["hob", "cooktop"], match: ["hob", "cooktop"] },
  ];
  for (const b of buckets) {
    if (b.keys.some((k) => t.includes(k))) {
      const hit = lowered.find(
        (x) => !WASHER_DRYER_RE.test(x.l) && b.match.some((m) => x.l.includes(m)),
      );
      if (hit) return hit.n;
    }
  }
  return "";
}

/** EPREL product group to try first for a scanned appliance. */
export function inferProductGroup(productType?: string, category?: string): string {
  const t = `${productType || ""} ${category || ""}`.toLowerCase();
  if (t.includes("dishwash")) return "dishwashers2019";
  if (t.includes("fridge") || t.includes("refriger") || t.includes("freezer"))
    return "refrigeratingappliances2019";
  if (WASHER_DRYER_RE.test(t)) return "washerdriers2019";
  if (t.includes("dryer") || t.includes("tumble")) return "tumbledryers2019";
  if (t.includes("oven")) return "ovens2015";
  return "washingmachines2019";
}

const WASHER_DRYER_FIXABLE = new Set(["dryer", "tumble dryer", "washing machine"]);

/**
 * FIX-289: what to change on an EXISTING appliance a batch row matched.
 * - dryer_technology: the type shield wins over the stored value and EPREL
 *   (a conflict is warned by resolveScannedDryingTechnology); without a scan
 *   value a stored value is kept, and only an empty one is filled from EPREL.
 * - category: only a scanned washer dryer stored as Dryer / Tumble Dryer /
 *   Washing Machine moves to the company's Washer Dryer category.
 * Returns only the fields that actually change.
 */
export function existingAppliancePatch(input: {
  stored: { dryer_technology: string | null; category: string | null };
  scanTechnology: DryingTechnology | null | undefined;
  eprelTechnology: DryingTechnology | null | undefined;
  productType: string | undefined;
  categoryNames: string[];
}): { dryer_technology?: DryingTechnology; category?: string } {
  const { stored, scanTechnology, eprelTechnology, productType, categoryNames } = input;
  const patch: { dryer_technology?: DryingTechnology; category?: string } = {};

  const storedTech = (stored.dryer_technology || null) as DryingTechnology | null;
  const tech = scanTechnology
    ? resolveScannedDryingTechnology(scanTechnology, storedTech || eprelTechnology)
    : storedTech || eprelTechnology || null;
  if (tech && tech !== storedTech) patch.dryer_technology = tech;

  if (productType && WASHER_DRYER_RE.test(productType)) {
    const current = (stored.category || "").trim().toLowerCase();
    if (WASHER_DRYER_FIXABLE.has(current)) {
      const wd = matchCompanyCategory(productType, categoryNames);
      if (wd && wd !== stored.category) patch.category = wd;
    }
  }
  return patch;
}
